<template>
	<div
		:class="[
			'nav',
			'search',
			showSearch ? 'is-open' : '',
			showResults ? 'is-result-list-visible' : ''
		]"
		ref="globalsearchHeader"
		data-testid="globalsearchHeader"
	>
		<div class="search__forms">
			<div
				class="search__form"
				data-testid="globalsearch-form"
				v-show="showSearch"
			>
				<div class="search__form-options">
					<VueGlobalSearchCategoryList
						v-model="selectedArea"
						:options="areas"
						@change="handleAreaChange"
						data-testid="globalsearch-area-dropdown"
						v-log="{
							target: 'Global Search Select Area',
							event: 'change'
						}"
					/>
				</div>
				<div class="search__form-input-container">
					<input
						class="search__form-input"
						ref="globalsearchInput"
						data-testid="globalsearch-input"
						type="text"
						v-model="searchText"
						placeholder="Search..."
						@input="search"
						v-on:keyup.enter="
							navigateToUltimateSearch(selectedArea)
						"
					/>
				</div>
				<button
					class="search__form-btn"
					data-testid="globalsearch-search-btn"
					@click="navigateToUltimateSearch(selectedArea)"
				>
					<i class="icon icon-search-2"></i>
				</button>
			</div>
			<button
				class="search__btn"
				data-testid="globalsearch-toggle-btn"
				@click="toggleSearch"
			>
				<span class="search__btn-icon-tablet-up">
					<i
						:class="[
							'icon',
							showSearch ? 'icon-remove-2' : 'icon-search-2'
						]"
					></i>
				</span>
				<span class="search__btn-icon-mobile">
					<i class="icon icon-search-2"></i>
				</span>
			</button>
		</div>
		<div
			class="search__results"
			data-testid="globalsearch-results"
			v-show="showResults"
		>
			<div v-if="loading">
				<Loader variant="dots" />
			</div>
			<div data-testid="globalsearch-has-results" v-if="hasResults">
				<ul class="search__results-list">
					<li
						class="search__results-item"
						v-for="section in globalSearchResults.categories"
						:key="section.areaName"
					>
						<VueGlobalSearchSection
							@globalsearch-clicked-result="hideSearch"
							:globalSearchSection="section"
							:ultimateSearchLink="
								ultimateSearchLink(section.area)
							"
						/>
						<a
							class="search__link-see-more"
							@click="hideSearch"
							:data-testid="
								`globalsearch-result-see-more-${section.area}`
							"
							target="_blank"
							:href="ultimateSearchLink(section.area)"
							v-show="section.totalCount > 3"
							>See more {{ section.areaName }}...</a
						>
					</li>
				</ul>
			</div>
			<div
				class="search__no-results"
				v-if="!hasResults && !loading"
				data-testid="globalsearch-no-results"
			>
				<span>
					No results found.
				</span>
			</div>
		</div>
	</div>
</template>

<script>
import VueGlobalSearchSection from './vue-global-search-section.vue';
import getCompanyId from '@/composables/get-companyid';
import buildRoute from '@/composables/build-route';
import VueGlobalSearchCategoryList from './vue-global-search-category-list.vue';
import { mapGetters } from 'vuex';
import GlobalSearchCategory from '@/enumerations/global-search-categories';
import parameterizeArray from '@/utils/parameterize-array';
import Loader from '@/components/visual/loader/loader.vue';
import loggingMixin from '@/mixins/logging-mixin';
import apiService from '@/services/api-service';
import consoleLog from '@/utils/console-log';
import baseComponentMixin from '@/mixins/base-component-mixin';

export default {
	name: 'VueGlobalSearch',
	components: {
		VueGlobalSearchSection,
		VueGlobalSearchCategoryList,
		Loader
	},
	data() {
		return {
			loading: false,
			abortControllers: [],
			globalSearchResults: {},
			searchText: '',
			selectedArea: '',
			showSearch: false,
			showResults: false,
			globalSearchCategory: GlobalSearchCategory,
			companyId: getCompanyId(this.$route),
			areas: [
				{
					text: 'Accounting',
					value: GlobalSearchCategory.Accounting,
					selected: false,
					hasDivider: true,
					categories: [
						GlobalSearchCategory.JournalDetails,
						GlobalSearchCategory.Receipts,
						GlobalSearchCategory.PurchaseOrders,
						GlobalSearchCategory.AccountsPayable,
						GlobalSearchCategory.VehicleSales
					]
				},
				{
					text: 'All',
					value: GlobalSearchCategory.All,
					selected: true
				},
				{
					text: 'Appointments',
					value: GlobalSearchCategory.Appointments,
					selected: false
				},
				{
					text: 'Customers',
					value: GlobalSearchCategory.Customers,
					selected: false
				},
				{
					text: 'Deals',
					value: GlobalSearchCategory.Deals,
					selected: false
				},
				{
					text: 'Desks',
					value: GlobalSearchCategory.Desks,
					selected: false
				},
				{
					text: 'Parts Invoices',
					value: GlobalSearchCategory.PartsInvoices,
					selected: false
				},
				{
					text: 'Phone Numbers',
					value: GlobalSearchCategory.CustomerPhoneNumbers,
					selected: false
				},
				{
					text: 'Prospects',
					value: GlobalSearchCategory.Prospects,
					selected: false
				},
				{
					text: 'Repair Orders',
					value: GlobalSearchCategory.RepairOrders,
					selected: false
				},
				{
					text: 'Vehicles',
					value: GlobalSearchCategory.Vehicles,
					selected: false
				},
				{
					text: 'Vendors',
					value: GlobalSearchCategory.Vendors,
					selected: false
				}
			]
		};
	},
	mixins: [loggingMixin, baseComponentMixin],
	created() {
		this.setQuickSearchDefault();
	},
	computed: {
		...mapGetters('user', ['vueAppBase', 'quickSearchDefault']),
		hasResults() {
			return (
				this.globalSearchResults.categories &&
				this.globalSearchResults.categories.length
			);
		}
	},
	methods: {
		setQuickSearchDefault() {
			let defVal = '';
			const defText = this.quickSearchDefault;

			if (defText) {
				const areaIndex = this.areas.findIndex(a => a.text === defText);

				if (areaIndex > 0) {
					defVal = this.areas[areaIndex].value;
				}
			}

			//console.log(defVal);

			this.selectedArea = defVal;
		},
		async loadGlobalSearchResults() {
			const route = buildRoute(
				'/globalsearch/company/{companyId}/autocomplete?searchText={searchText}&maxCount=3&area={area}&companyId={cid}',
				{
					companyId: this.companyId,
					cid: this.companyId,
					searchText: this.searchText,
					area: this.selectedArea
				}
			);

			this.loading = true;

			apiService.getWithAbort(
				route,
				this.uid,
				this,
				function(response) {
					//success handler
					this.globalSearchResults = response;
				},
				function() {
					//finally handler
					this.loading = false;
				},
				function(error) {
					//error handler
					consoleLog('loadGlobalSearchResults cancelled', error);
				}
			);
		},
		async getGlobalSearchAction(area) {
			const route = buildRoute(
				'/globalsearch/company/{companyId}/autocomplete?searchText={searchText}&maxCount=3&companyId={cid}',
				{
					companyId: this.companyId,
					cid: this.companyId,
					searchText: this.searchText
				}
			);
			const areaQueryParamString = parameterizeArray(
				'Area',
				this.areas.find(cat => cat.value === area).categories
			);

			this.loading = true;

			apiService.getWithAbort(
				route + '&' + areaQueryParamString,
				this.uid,
				this,
				function(response) {
					//success handler
					this.globalSearchResults = response;
				},
				function() {
					//finally handler
					this.loading = false;
				},
				function(error) {
					//error handler
					consoleLog('loadGlobalSearchResults cancelled', error);
				}
			);
		},
		async search() {
			if (this.searchText.length > 2) {
				this.showSearchResults();
				if (this.selectedArea == this.globalSearchCategory.Accounting)
					await this.getGlobalSearchAction(this.selectedArea);
				else await this.loadGlobalSearchResults();
			} else {
				this.hideSearchResults();
			}
		},
		ultimateSearchLink(area) {
			let base = this.vueAppBase;

			if (!base) {
				base = '/';
			}

			return `${base}company/${this.companyId}/globalsearch/search/${this.searchText}/area/${area}`;
		},
		navigateToUltimateSearch(area) {
			let link;

			if (this.searchText.length) {
				link = this.ultimateSearchLink(area);
				this.hideSearch();
				window.open(link, '_blank');
			}
		},
		toggleSearch() {
			if (this.showSearch) {
				this.hideSearch();
			} else {
				this.showSearch = true;
				this.focusOnInput();
			}
		},
		hideSearchResults() {
			this.showResults = false;
			document.removeEventListener('mouseup', this.globalMouseUpHandler);
		},
		showSearchResults() {
			if (this.showSearch) {
				this.showResults = true;
				document.addEventListener('mouseup', this.globalMouseUpHandler);
			}
		},
		hideSearch() {
			this.showSearch = false;
			this.globalSearchResults = {};
			this.searchText = '';
			this.hideSearchResults();
		},
		globalMouseUpHandler(e) {
			const container = this.$refs.globalsearchHeader;
			if (
				e &&
				container &&
				!container.contains(e.target) &&
				this.showResults
			) {
				this.hideSearchResults();
			}
		},
		focusOnInput() {
			this.$nextTick(() => {
				const input = this.$refs.globalsearchInput;
				input.focus();
			});
		},
		handleAreaChange() {
			this.search();
			this.focusOnInput();
		}
	}
};
</script>

<style lang="scss" scoped>
@include scrollbar('borderless');

.search {
	display: flex;
	justify-content: center;
	height: 100%;
	max-width: 420px;
	min-width: 42px;
	position: relative;
	font-size: $txt-font-size;
	margin-left: auto;

	::placeholder {
		color: black;
	}

	&.is-open {
		.search__btn {
			justify-content: center;
			background: var(--bg-brand-1);
			color: var(--bg-brand-1-fg-3);

			@include mq($from: md-l) {
				&:hover {
					background: var(--menu-list-bg-hover);
					color: var(--menu-list-fg-hover);
				}
			}
		}

		@include mq($from: sm-l) {
			.search__btn {
				background: none;
				color: inherit;
			}
		}
	}

	&.is-result-list-visible {
		@include mq($from: sm-l) {
			.search__form-input {
				border-bottom-right-radius: 0;
			}
		}
	}

	&__forms {
		display: inline-flex;
		width: 100%;
		align-items: center;
		z-index: $z-level1-2;
	}

	&__form {
		display: inline-flex;
		align-items: flex-start;
		position: fixed;
		width: 100%;
		height: calc(100vh - 49px);
		top: 49px;
		left: 0;
		z-index: $z-level1-5;
		padding: $padding-md;
		background: var(--bg-0);
		color: var(--bg-0-fg-4);

		@include mq($from: sm-l) {
			align-items: center;
			position: inherit;
			height: initial;
			margin-left: auto;
			top: initial;
			left: initial;
			padding: 0;
		}

		&-options {
			.dropdown__body {
				color: red;
			}
		}
	}

	&__btn {
		display: flex;
		align-items: center;
		height: 100%;
		justify-content: center;
		min-width: 42px;
		padding: 6px $padding-sm;
		margin-left: $gutter-xs;
		@include clear-btn-styles;
		font-size: 18px;
		background: var(--bg-0);
		color: var(--bg-0-fg-4);
		border-radius: 0;

		@include mq($from: md-l) {
			&:hover {
				background: var(--menu-list-bg-hover);
				color: var(--menu-list-fg-hover);
			}
		}

		&-icon-tablet-up {
			display: none;
		}

		@include mq($from: sm-l) {
			&-icon-tablet-up {
				display: inline-block;
			}

			&-icon-mobile {
				display: none;
			}
		}

		@include header-focus-styles;
	}

	&__form-input-container {
		width: 100%;
		display: flex;
		align-items: center;
		position: relative;

		&::before {
			position: absolute;
			content: '';
			left: 0;
			top: 50%;
			transform: translateY(-50%);
			height: 20px;
			width: 1px;
			background: var(--nav-field-fg-extras); //here
		}

		&:focus-within {
			&::before {
				display: none;
			}

			&::after {
				content: '';
				@include transform-vertical-center;
				width: calc(100% + 34px);
				height: calc(100% + 1px);
				border-radius: 2px;
				border: $focus-width $focus-style
					var(--nav-brand-1-border-active);
				pointer-events: none;
				z-index: $z-level1-1;
			}
		}
	}

	&__form-input {
		width: 100%;
		border: $field-border;
		border-left: none;
		border-right: none;
		padding: $padding-sm;
		border-radius: 0;
		line-height: 16px;

		&:focus,
		&:active {
			outline: none;
			border-color: transparent;
		}
	}

	&__form-btn {
		display: inline-flex;
		position: relative;
		@include clear-btn-styles;
		border: $field-border;
		border-left: none;
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
		border-top-right-radius: 4px;
		border-bottom-right-radius: 4px;
		padding: $padding-sm;
		color: var(--bg-0-fg-3);
		font-size: 16px;
		line-height: 16px;

		&:hover {
			color: var(--bg-0-fg-4);
		}

		&:focus,
		&:visited {
			outline: none;
			border-color: transparent;

			&:after {
				content: '';
				@include transform-full-center;
				width: calc(100% + 1px);
				height: calc(100% + 3px);
				border: $focus-width $focus-style var(--brand-2);
				z-index: $z-level1-1;
			}
		}
	}

	&__results {
		position: fixed;
		width: 100%;
		height: calc(100% - 108px);
		top: 108px;
		left: 0;
		border-top: $menu-list-border;
		overflow-y: auto;
		z-index: $z-level1-1;

		@include mq($from: sm-l) {
			position: absolute;
			width: 266px;
			height: auto;
			max-height: 450px;
			top: 40px;
			left: auto;
			right: 44px;
			background: var(--bg-0);
			color: var(--bg-0-fg-4);
			border: 1px solid $menu-list-border;
			border-radius: $submenu-border-radius;
			@include medium-shadow;
		}
	}

	&__no-results {
		padding: $padding-md;
		text-align: center;
	}

	&__link-see-more {
		display: inline-block;
		width: 100%;
		padding: $padding-md;
		padding-left: 32px;
		color: var(--link);

		&:hover {
			color: var(--link-hover);
			text-decoration: underline;
		}

		@include header-focus-styles;
	}
}
</style>
